import { Badge, Group, SegmentedControl, Stack, Table, Title } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminShell } from '../../../components/admin-shell/AdminShell';
import { AdminTableSection } from '../../../components/admin-shell/AdminTableSection';
import { usePaginatedQuery } from '../_hooks';

type DisputeState = 'opened' | 'acknowledged' | 'under_review' | 'resolved' | 'closed';

interface DisputeRow {
  id: string;
  short_id: string;
  exchange_id: string;
  state: DisputeState;
  category: string;
  opened_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  sla: 'ok' | 'at_risk' | 'breached';
}

export const Route = createFileRoute('/admin/disputes/')({
  component: AdminDisputesList,
});

function AdminDisputesList() {
  const [state, setState] = useState<DisputeState | 'all'>('opened');

  const { data, isLoading, error, page, setPage, totalPages, bindFilter } = usePaginatedQuery<DisputeRow>({
    queryKey: ['admin', 'disputes'],
    endpoint: '/api/admin/disputes',
    filters: { state: state === 'all' ? null : state },
  });

  // T3 — overdue (past 48h ack SLA, across opened/acknowledged/under_review)
  // surfaces above the queue so SLA-slip is visible regardless of which
  // state filter is selected. Server computes against the breach threshold;
  // see worker/routes/admin/disputes.ts loadOverdueDisputeCount.
  const overdueCount = data?.overdue_count ?? 0;

  return (
    <AdminShell>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" align="center">
          <Title order={2}>Disputes</Title>
          {overdueCount > 0 ? (
            <Badge color="red" variant="filled" size="lg" radius="sm">
              Overdue: {overdueCount}
            </Badge>
          ) : null}
        </Group>
        <SegmentedControl
          value={state}
          onChange={bindFilter((v: string) => setState(v as DisputeState | 'all'))}
          data={[
            { label: 'Open', value: 'opened' },
            { label: 'Acknowledged', value: 'acknowledged' },
            { label: 'Under review', value: 'under_review' },
            { label: 'Resolved', value: 'resolved' },
            { label: 'Closed', value: 'closed' },
            { label: 'All', value: 'all' },
          ]}
        />

        <AdminTableSection<DisputeRow>
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No disputes in this state."
          header={
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Opened</Table.Th>
              <Table.Th>SLA</Table.Th>
              <Table.Th>State</Table.Th>
            </Table.Tr>
          }
          renderRow={(d) => (
            <Table.Tr key={d.id}>
              <Table.Td>
                <Link to="/admin/disputes/$id" params={{ id: d.id }}>
                  {d.short_id}
                </Link>
              </Table.Td>
              <Table.Td>{d.category}</Table.Td>
              <Table.Td>{relative(d.opened_at)}</Table.Td>
              <Table.Td>
                <Badge color={slaColor(d.sla)} variant="light">
                  {d.sla}
                </Badge>
              </Table.Td>
              <Table.Td>{d.state}</Table.Td>
            </Table.Tr>
          )}
        />
      </Stack>
    </AdminShell>
  );
}

function slaColor(sla: string): string {
  if (sla === 'breached') return 'red';
  if (sla === 'at_risk') return 'yellow';
  return 'tide';
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
